"use client";

/**
 * DialIQ — Smart Redial Panel
 *
 * One row per redial window: what's dialable now, and when the rest opens in
 * the agent's own clock. Yesterday's unanswered leads sit on top until the
 * agent adds them to today.
 *
 * Opened from the Leads header, the /m More menu, and the 6 AM digest
 * notification (/leads?dialiqDigest=<date>).
 *
 * Integrations:
 *   - Unified All-in-One Dialer: "Dial now" pushes the window's live leads
 *     into the dialer queue via addLeadsToDialer() and closes the panel.
 *   - Settings: deep-links to the Dialer preferences tab.
 */

import { memo, useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import dayjs from "dayjs";
import { Moon, RefreshCcw, SlidersHorizontal, Sun, Sunrise, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { useToast } from "@/app/_module/hooks/use-toast";
import redialService from "@/app/_module/services/redialService";
import { DialIQMark } from "./DialIQMark";
import {
  WINDOW_META,
  type DailyDigest,
  type RedialWindow,
  type RedialWindowCount,
} from "@/app/_module/services/redialService.types";

// ────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────

const WINDOW_ORDER: RedialWindow[] = ["am", "pm", "evening"];

// One dial session is capped at 500 leads — must stay in sync with
// UnifiedDialerContext's MAX_SESSION_LEADS. When a window has more than
// this ready, the agent dials the first 500, then clicks again for the
// rest (answered leads drop out, so the next click surfaces fresh leads).
const DIAL_BATCH_SIZE = 500;

// Compact count formatting so big queues don't blow out the layout:
// 847 → "847", 1500 → "1.5K", 10000 → "10K", 10500 → "10.5K".
const compactCount = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const fmt = (n: number): string => compactCount.format(n);
const plural = (n: number, noun: string) => `${fmt(n)} ${noun}${n === 1 ? "" : "s"}`;

const ICON_MAP = { sunrise: Sunrise, sun: Sun, moon: Moon } as const;

const iconButton = cn("h-9 w-9 text-gray-500 hover:text-gray-900", focusRing);

// Non-breaking spaces keep "11:41 AM" and "8–10 AM" on one line.
const NBSP = "\u00A0";

/** When a window opens, on the agent's clock: "at 3:00 PM (in 25 min)", "tomorrow at 8:00 AM". */
function opensPhrase(iso: string): string {
  const now = dayjs();
  const at = dayjs(iso);
  const mins = at.diff(now, "minute");
  // Opened since the last fetch; the next refetch flips the row to live.
  if (mins <= 0) return "any minute";
  const clock = at.format(`h:mm[${NBSP}]A`);
  if (at.isSame(now, "day")) return mins < 60 ? `at ${clock} (in ${mins}${NBSP}min)` : `at ${clock}`;
  if (at.isSame(now.add(1, "day"), "day")) return `tomorrow at ${clock}`;
  return `${at.format("ddd")} at ${clock}`;
}

// ────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────

interface DialIQPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DialIQPanelImpl({ open, onOpenChange }: DialIQPanelProps) {
  const router = useRouter();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { addLeadsToDialer } = useUnifiedDialer();
  const [dialing, setDialing] = useState<RedialWindow | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const userTz =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "America/New_York";
  const today = dayjs().format("YYYY-MM-DD");

  // ─── Queries ────────────────────────────────────────────
  const { data: counts, isError: countsFailed, refetch: refetchCounts } = useQuery({
    queryKey: ["redial", "counts"],
    queryFn: redialService.getChipCounts,
    staleTime: 30_000,
    // Windows open while the panel sits open; re-check so a row goes live on its own.
    refetchInterval: 60_000,
    enabled: open, // only fetch when panel is open
  });

  const { data: digest } = useQuery({
    queryKey: ["redial", "digest", today],
    queryFn: () => redialService.getDigest(today, userTz),
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const pendingDigest =
    digest && !digest.alreadyApproved && digest.totalUnanswered > 0 ? digest : null;

  const totals = useMemo(() => {
    const sum = (key: "active" | "total") =>
      WINDOW_ORDER.reduce((n, w) => n + (counts?.[w][key] ?? 0), 0);
    // Same-format UTC ISO strings sort chronologically.
    const nextOpenAt =
      WINDOW_ORDER.map((w) => counts?.[w].nextOpenAt)
        .filter((t): t is string => !!t)
        .sort()[0] ?? null;
    return { live: sum("active"), queued: sum("total"), nextOpenAt };
  }, [counts]);

  // ─── Handlers ───────────────────────────────────────────
  const handleDialNow = useCallback(
    async (win: RedialWindow) => {
      const label = WINDOW_META[win].label.toLowerCase();
      setDialing(win);
      try {
        const result = await redialService.getList(win, { liveOnly: true, limit: DIAL_BATCH_SIZE });
        if (!result.leads || result.leads.length === 0) {
          toast({
            title: `No ${label} leads are ready right now`,
            description: `Their window opens at ${WINDOW_META[win].opensLabel} in each lead's time zone.`,
          });
          return;
        }
        // Mark these leads as 'dialiq' source — drives Predictive Dial activation
        // in the Power Dialer when the agent's agency is in the rollout cohort.
        addLeadsToDialer(result.leads as any, "dialiq");
        toast({
          title: `${plural(result.leads.length, `${label} lead`)} sent to the dialer`,
          description: "Opening the dialer…",
        });
        onOpenChange(false);
      } catch {
        toast({
          title: "Couldn't load the redial list",
          description: "Try again in a moment.",
          variant: "destructive",
        });
      } finally {
        setDialing(null);
      }
    },
    [addLeadsToDialer, toast, onOpenChange],
  );

  const approveMutation = useMutation({
    mutationFn: (d: DailyDigest) =>
      redialService.approveDigest({
        date: today,
        leadIds: {
          am: d.am.map((l) => l.id),
          pm: d.pm.map((l) => l.id),
          evening: d.evening.map((l) => l.id),
        },
        snapshot: { am: d.am, pm: d.pm, evening: d.evening },
      }),
    onSuccess: (res) => {
      const total = res.amCount + res.pmCount + res.eveningCount;
      toast({ title: `Added ${plural(total, "lead")} to today's windows` });
    },
    onError: () =>
      toast({
        title: "Couldn't add yesterday's leads",
        description: "Refresh and try again.",
        variant: "destructive",
      }),
    // Also on error: a 409 means another tab already added them, so refresh hides the card.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["redial"] }),
  });

  const handleGoToSettings = useCallback(() => {
    onOpenChange(false);
    router.push("/settings?tab=account&accountTab=preferences&prefTab=dialer");
  }, [onOpenChange, router]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refetchCounts(),
        queryClient.invalidateQueries({ queryKey: ["redial", "digest", today] }),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [refetchCounts, queryClient, today]);

  // A failed background refetch keeps the last good counts on screen.
  const loadFailed = !counts && countsFailed;

  let summary: string;
  if (!counts) {
    summary = loadFailed ? "Couldn't load your redial queue." : "Loading your redial queue…";
  } else if (totals.live > 0) {
    summary = `${plural(totals.live, "lead")} ready to dial now.`;
  } else if (totals.queued > 0) {
    summary = `${plural(totals.queued, "lead")} queued. ${
      totals.nextOpenAt
        ? `The next window opens ${opensPhrase(totals.nextOpenAt)}.`
        : "Each window opens on the lead's local time."
    }`;
  } else if (pendingDigest) {
    summary = "Nothing queued yet.";
  } else {
    summary = "Nothing to redial yet. Leads show up here when calls go unanswered.";
  }

  // ─── Render ─────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Breakpoint padding/width variants pinned too: tailwind-merge keeps the base md:p-6 / lg:max-w-2xl otherwise.
          grid-cols-1: the base grid's auto column grew to the longest lead name at 320px. */}
      <DialogContent
        ref={contentRef}
        // Focus the dialog itself, not Refresh: no stray focus ring when it opens from the
        // digest link. Tab still lands on Refresh first.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
        className="grid-cols-1 gap-0 p-0 md:p-0 sm:max-w-lg md:max-w-lg lg:max-w-lg xl:max-w-lg [&>button:last-of-type]:hidden"
      >
        <div className="px-5 pb-4 pt-5">
          <div className="flex items-start gap-3">
            {/* !px-0: the shell pads every dialog h2 44px to clear the ✕ this panel hides */}
            <DialogTitle className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 !px-0 !text-[17px] leading-6 text-gray-900">
              <span className="relative inline-flex pr-3.5">
                Dial
                <DialIQMark size={11} className="absolute right-0 top-0 text-primary" />
              </span>
              <span className="sr-only">IQ</span>
              <span className="text-sm font-normal tracking-normal text-gray-500">Smart redial</span>
            </DialogTitle>
            <div className="-mr-2 -mt-1.5 flex shrink-0 items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label="Refresh"
                title="Refresh"
                className={iconButton}
              >
                <RefreshCcw className={cn("h-4 w-4", refreshing && "animate-spin")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleGoToSettings}
                aria-label="DialIQ settings"
                title="DialIQ settings"
                className={iconButton}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                title="Close"
                className={iconButton}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogDescription className="mt-1 text-gray-600">{summary}</DialogDescription>
        </div>

        {pendingDigest && (
          <DigestCard
            digest={pendingDigest}
            onApprove={() => approveMutation.mutate(pendingDigest)}
            isApproving={approveMutation.isPending}
          />
        )}

        {loadFailed ? (
          <div className="border-y border-gray-100 px-5 py-5">
            <p className="text-sm text-gray-600">Check your connection, then try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchCounts()}
              className={cn("mt-3", focusRing)}
            >
              Try again
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 border-y border-gray-100">
            {WINDOW_ORDER.map((w) => (
              <WindowRow
                key={w}
                window={w}
                count={counts?.[w]}
                loading={!counts}
                suggested={pendingDigest?.[w].length ?? 0}
                dialing={dialing === w}
                onDial={() => handleDialNow(w)}
              />
            ))}
          </ul>
        )}

        <p className="px-5 py-4 text-xs leading-relaxed text-gray-500">
          Windows run on each lead&apos;s local time. After 5 unanswered calls in one window
          within 30 days, DialIQ moves the lead to the next window.
        </p>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// WindowRow — one redial window: status line + Dial now when live
// ────────────────────────────────────────────────────────────

interface WindowRowProps {
  window: RedialWindow;
  count?: RedialWindowCount;
  loading: boolean;
  // Leads an UNAPPROVED digest suggests for this window. Shown so an empty
  // window reflects the pending digest instead of "Nothing queued" — they
  // haven't been added to the queue yet, so counts (redial_window) is still 0.
  suggested: number;
  dialing: boolean;
  onDial: () => void;
}

function WindowRow({ window: w, count, loading, suggested, dialing, onDial }: WindowRowProps) {
  const meta = WINDOW_META[w];
  const Icon = ICON_MAP[meta.iconName];
  const active = count?.active ?? 0;
  const total = count?.total ?? 0;
  const later = total - active;
  const live = active > 0;
  // nextOpenAt only covers leads that aren't live yet; older backends omit it.
  const opens = count?.nextOpenAt ? opensPhrase(count.nextOpenAt) : null;

  let status: ReactNode;
  if (loading) {
    status = (
      <span aria-hidden className="inline-block h-3.5 w-40 animate-pulse rounded bg-gray-100 align-middle" />
    );
  } else if (live) {
    status = (
      <>
        <span className="font-medium text-emerald-700">
          <span className="tabular-nums">{fmt(active)}</span> ready now
        </span>
        {later > 0 && (
          <>
            {" · "}
            <span className="tabular-nums">{fmt(later)}</span> more {opens ?? "later"}
          </>
        )}
        {active > DIAL_BATCH_SIZE && <>{` · ${DIAL_BATCH_SIZE} per batch`}</>}
      </>
    );
  } else if (total > 0) {
    status = (
      <>
        <span className="font-medium tabular-nums text-gray-900">{fmt(total)}</span> queued ·
        opens {opens ?? `at ${meta.opensLabel} their time`}
      </>
    );
  } else if (suggested > 0) {
    status = (
      <>
        <span className="font-medium tabular-nums text-gray-900">{fmt(suggested)}</span> suggested
        from yesterday
      </>
    );
  } else {
    status = "Nothing queued";
  }

  // Below ~10rem of text room (320px phones) the button wraps under the text, lined up with it.
  return (
    <li className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5", live && "bg-emerald-50/60")}>
      <Icon
        aria-hidden
        className={cn("h-4 w-4 shrink-0", live ? "text-emerald-600" : "text-gray-400")}
      />
      <div className="min-w-[10rem] flex-1">
        <p className="text-sm font-medium text-gray-900">
          {meta.label}{" "}
          <span className="font-normal text-gray-500">{meta.timeLabel.replace(" ", NBSP)}</span>
        </p>
        <p className="mt-0.5 text-sm text-gray-500">{status}</p>
      </div>
      {live && (
        <Button size="sm" onClick={onDial} disabled={dialing} className={cn("ml-7 shrink-0 sm:ml-0", focusRing)}>
          {dialing ? "Loading…" : `Dial ${fmt(Math.min(active, DIAL_BATCH_SIZE))} now`}
        </Button>
      )}
    </li>
  );
}

// ────────────────────────────────────────────────────────────
// DigestCard — yesterday's unanswered leads, until they're added
// ────────────────────────────────────────────────────────────

interface DigestCardProps {
  digest: DailyDigest;
  onApprove: () => void;
  isApproving: boolean;
}

function DigestCard({ digest, onApprove, isApproving }: DigestCardProps) {
  const [showList, setShowList] = useState(false);
  const n = digest.totalUnanswered;

  return (
    <section
      aria-label="Yesterday's unanswered leads"
      className="mx-5 mb-4 rounded-lg border border-blue-100 bg-blue-50/60 p-4"
    >
      <p className="text-sm font-medium text-gray-900">
        {plural(n, "lead")} didn&apos;t answer yesterday
      </p>
      <p className="mt-0.5 text-sm text-gray-600">
        DialIQ sorted them into today&apos;s windows by each lead&apos;s local time.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={onApprove} disabled={isApproving} className={focusRing}>
          {isApproving ? "Adding…" : `Add ${fmt(n)} to today`}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowList((s) => !s)}
          aria-expanded={showList}
          className={cn("text-gray-700 hover:bg-blue-100/60", focusRing)}
        >
          {showList ? "Hide list" : "See who"}
        </Button>
      </div>

      {showList && (
        <div className="mt-3 max-h-56 space-y-3 overflow-y-auto rounded-md border border-blue-100 bg-white p-3">
          {WINDOW_ORDER.filter((w) => digest[w].length > 0).map((w) => {
            const bucket = digest[w];
            return (
              <div key={w}>
                <p className="text-xs font-medium text-gray-500">
                  {WINDOW_META[w].label} · {fmt(bucket.length)}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {bucket.slice(0, 5).map((lead) => (
                    <li key={lead.id} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate text-gray-900">
                        {lead.name || lead.phone || "Unnamed lead"}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-gray-500">
                        {[lead.state, lead.localTime].filter(Boolean).join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
                {bucket.length > 5 && (
                  <p className="mt-0.5 text-xs text-gray-500">and {fmt(bucket.length - 5)} more</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Export
// ────────────────────────────────────────────────────────────

export const DialIQPanel = memo(DialIQPanelImpl);
export default DialIQPanel;
